import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Save, X, Plus, Send, ArrowLeft, Trash2, Power, PowerOff, Rss, Play, Zap, FlaskConical } from "lucide-react";
import { ScreenTitle } from "@/components/ScreenTitle";

function ChipList({ label, testid, items, onChange }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (!v || items.includes(v)) { setInput(""); return; }
    onChange([...items, v]); setInput("");
  };
  const remove = (i) => {
    const next = items.slice(); next.splice(i, 1); onChange(next);
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
        {label} · {items.length}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((it, idx) => (
          <span key={it + idx}
                className="inline-flex items-center gap-1 bg-black text-white text-[11px] font-mono px-2 py-1"
                data-testid="chip">
            {it}
            <button onClick={() => remove(idx)} className="hover:text-[var(--accent)] leading-none">
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input type="text" value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Ajouter…" data-testid="chip-input"
          className="flex-1 min-h-[40px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
        <button onClick={add} data-testid="chip-add"
          className="min-h-[40px] px-3 border border-black bg-white hover:bg-black hover:text-white flex items-center gap-1 text-[11px] uppercase tracking-widest font-mono">
          <Plus className="w-3.5 h-3.5" /> Ajouter
        </button>
      </div>
    </div>
  );
}

export default function PoliticsCategoryDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cat, setCat] = useState(null);
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [newFeed, setNewFeed] = useState({ name: "", url: "" });
  const [probeResult, setProbeResult] = useState(null);   // {ok, items_read, counts, items, error}
  const [probing, setProbing] = useState(false);

  const load = async () => {
    try {
      const [c, f] = await Promise.all([
        api.get(`/politics/categories`),
        api.get(`/politics/feeds`, { params: { category_id: id } }),
      ]);
      const one = c.data.find((x) => x.id === id);
      if (!one) throw new Error("Catégorie introuvable");
      setCat(one); setFeeds(f.data);
    } catch (e) { toast.error("Chargement impossible"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const patch = (k, v) => setCat({ ...cat, [k]: v });

  const save = async (body) => {
    setSaving(true);
    try {
      const r = await api.patch(`/politics/categories/${id}`, body);
      setCat(r.data);
      toast.success("Enregistré");
    } catch (e) { toast.error("Sauvegarde impossible"); }
    finally { setSaving(false); }
  };

  const tick = async () => {
    try {
      await api.post(`/politics/categories/${id}/tick`);
      toast.success("Tick lancé pour cette catégorie");
    } catch (e) { toast.error("Impossible"); }
  };

  const testTg = async () => {
    try {
      const r = await api.post(`/politics/categories/${id}/telegram-test`);
      toast[r.data.ok ? "success" : "error"](r.data.ok ? "Test envoyé" : "Pas de réponse");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test impossible"); }
  };

  const probeNewFeed = async () => {
    const u = newFeed.url.trim();
    if (!u) { toast.error("URL requise"); return; }
    setProbing(true); setProbeResult(null);
    try {
      const r = await api.post("/politics/probe-url", { category_id: id, url: u });
      setProbeResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test impossible");
    } finally { setProbing(false); }
  };

  const addFeed = async () => {
    if (!newFeed.name.trim() || !newFeed.url.trim()) { toast.error("Nom & URL requis"); return; }
    try {
      await api.post("/politics/feeds", { category_id: id, name: newFeed.name.trim(), url: newFeed.url.trim(), active: true });
      toast.success("Flux ajouté");
      setNewFeed({ name: "", url: "" });
      setProbeResult(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Flux invalide"); }
  };

  const toggleFeed = async (f) => {
    try { await api.patch(`/politics/feeds/${f.id}`, { active: !f.active }); load(); }
    catch (e) { toast.error("Impossible"); }
  };

  const removeFeed = async (f) => {
    if (!window.confirm(`Supprimer "${f.name}" ?`)) return;
    try { await api.delete(`/politics/feeds/${f.id}`); load(); toast.success("Flux supprimé"); }
    catch (e) { toast.error("Suppression impossible"); }
  };

  const deleteCategory = async () => {
    if (!window.confirm(`Supprimer la catégorie "${cat.name}" et TOUS ses flux/alertes ?`)) return;
    try { await api.delete(`/politics/categories/${id}`); toast.success("Supprimée"); nav("/veille/categories"); }
    catch (e) { toast.error("Suppression impossible"); }
  };

  const purgeDedup = async () => {
    if (!window.confirm(
      `Purger la mémoire de déduplication de "${cat.name}" ?\n\n` +
      `Cela permettra à la veille de RE-évaluer tous les items récents des flux ` +
      `de cette catégorie contre les mots-clés à jour. Les alertes déjà créées ` +
      `ne sont pas touchées.`
    )) return;
    try {
      const r = await api.delete(`/politics/categories/${id}/dedup`);
      toast.success(`${r.data.removed} entrée(s) purgée(s) sur ${r.data.feeds} flux`);
    } catch (e) { toast.error("Purge impossible"); }
  };

  const purgeAndTick = async () => {
    try {
      const r = await api.post(`/politics/categories/${id}/purge-and-tick`);
      toast.success(
        `Dédup purgé (${r.data.removed}) + tick relancé — les alertes vont apparaître d'ici quelques secondes`
      );
    } catch (e) { toast.error("Impossible"); }
  };

  if (loading || !cat) {
    return <div className="px-5 py-10 text-sm text-[var(--text-muted)]">Chargement…</div>;
  }

  return (
    <div data-testid="politics-category-detail">
      <div className="px-5 pt-4">
        <button onClick={() => nav("/veille/categories")} data-testid="back-cats"
          className="min-h-[36px] px-2 flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-black">
          <ArrowLeft className="w-3.5 h-3.5" /> Retour catégories
        </button>
      </div>
      <ScreenTitle
        eyebrow={<span className="flex items-center gap-2">
          <span className="w-3 h-3" style={{ background: cat.color }} />
          Catégorie · {cat.active ? "active" : "inactive"}
        </span>}
        title={cat.name}
        right={
          <button onClick={tick} data-testid="cat-tick"
            className="min-h-[44px] px-3 flex items-center gap-2 border border-black bg-black text-white text-[11px] uppercase tracking-widest font-mono hover:bg-[#222]">
            <Play className="w-4 h-4" strokeWidth={1.75} /> Ticker
          </button>
        }
      />

      <div className="px-5 pb-5 space-y-6">
        {/* General */}
        <section className="border border-[var(--border-default)] p-4 space-y-3 bg-white">
          <h3 className="font-serif text-lg">Général</h3>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Nom</div>
            <input type="text" value={cat.name} onChange={(e) => patch("name", e.target.value)}
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Couleur</span>
            <input type="color" value={cat.color} onChange={(e) => patch("color", e.target.value)}
              className="w-16 h-10 border border-[var(--border-default)]" />
            <span className="font-mono text-xs">{cat.color}</span>
          </div>
          <label className="flex items-center gap-3 min-h-[44px] text-sm">
            <input type="checkbox" checked={cat.active} onChange={(e) => patch("active", e.target.checked)}
              className="w-5 h-5 accent-black" data-testid="cat-active" />
            Catégorie activée
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Fréquence (secondes)</div>
              <input type="number" min={60} max={3600} value={cat.poll_interval_seconds}
                onChange={(e) => patch("poll_interval_seconds", parseInt(e.target.value, 10) || 180)}
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
            </div>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Fraîcheur (h)</div>
              <input type="number" min={1} max={72} value={cat.max_item_age_hours}
                onChange={(e) => patch("max_item_age_hours", parseInt(e.target.value, 10) || 24)}
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Dédup (min)</div>
              <input type="number" min={5} max={180} value={cat.dedup_window_minutes}
                onChange={(e) => patch("dedup_window_minutes", parseInt(e.target.value, 10) || 30)}
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
            </div>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Silence de (UTC)</div>
              <input type="time" value={cat.quiet_from || ""} onChange={(e) => patch("quiet_from", e.target.value || null)}
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
            </div>
            <div>
              <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Silence à</div>
              <input type="time" value={cat.quiet_to || ""} onChange={(e) => patch("quiet_to", e.target.value || null)}
                className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => save({
              name: cat.name, color: cat.color, active: cat.active,
              poll_interval_seconds: cat.poll_interval_seconds,
              max_item_age_hours: cat.max_item_age_hours,
              dedup_window_minutes: cat.dedup_window_minutes,
              quiet_from: cat.quiet_from, quiet_to: cat.quiet_to,
            })} disabled={saving} data-testid="save-general"
              className="min-h-[44px] px-4 flex items-center gap-2 bg-black text-white text-[11px] uppercase tracking-widest font-mono hover:bg-[#222] disabled:opacity-60">
              <Save className="w-4 h-4" /> Enregistrer
            </button>
            <button onClick={purgeAndTick} data-testid="purge-and-tick"
              title="Purge la mémoire de dédup ET relance immédiatement un tick — feedback instantané pour calibrer les mots-clés"
              className="min-h-[44px] px-4 flex items-center gap-2 bg-[var(--accent)] text-white border border-[var(--accent)] hover:bg-[var(--accent-strong)] hover:border-[var(--accent-strong)] text-[11px] uppercase tracking-widest font-mono">
              <Zap className="w-4 h-4" /> Purger + Tick
            </button>
            <button onClick={purgeDedup} data-testid="purge-dedup"
              title="Purge seule, sans relancer de tick (attend le prochain passage automatique)"
              className="min-h-[44px] px-4 flex items-center gap-2 border border-[var(--border-default)] text-[var(--text-muted)] hover:bg-black hover:text-white hover:border-black text-[11px] uppercase tracking-widest font-mono">
              <Trash2 className="w-4 h-4" /> Purger seul
            </button>
          </div>
        </section>

        {/* Feeds */}
        <section className="border border-[var(--border-default)] p-4 space-y-3 bg-white">
          <h3 className="font-serif text-lg flex items-center gap-2"><Rss className="w-4 h-4" /> Flux ({feeds.length})</h3>
          <div className="space-y-2">
            <input type="text" placeholder="Nom du flux" value={newFeed.name}
              onChange={(e) => setNewFeed({ ...newFeed, name: e.target.value })}
              data-testid="new-feed-name"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
            <input type="url" placeholder="https://…/rss.xml" value={newFeed.url}
              onChange={(e) => { setNewFeed({ ...newFeed, url: e.target.value }); setProbeResult(null); }}
              data-testid="new-feed-url"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
            <div className="grid grid-cols-2 gap-2">
              <button onClick={probeNewFeed} disabled={probing || !newFeed.url.trim()} data-testid="new-feed-probe"
                className="min-h-[44px] flex items-center justify-center gap-2 border border-black bg-white hover:bg-black hover:text-white disabled:opacity-50 text-[11px] uppercase tracking-widest font-mono">
                <FlaskConical className="w-4 h-4" /> {probing ? "Test…" : "Tester avant"}
              </button>
              <button onClick={addFeed} data-testid="new-feed-save"
                className="min-h-[44px] flex items-center justify-center gap-2 bg-black text-white border border-black hover:bg-[#222] text-[11px] uppercase tracking-widest font-mono">
                <Plus className="w-4 h-4" /> Ajouter
              </button>
            </div>

            {probeResult && (
              <div className="mt-2 border border-[var(--border-default)] p-3 bg-[var(--bg-app)]" data-testid="probe-preview">
                {!probeResult.ok ? (
                  <div className="text-sm text-[var(--accent-strong)]">
                    Flux illisible : <code className="font-mono text-xs">{probeResult.error}</code>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-4 gap-2 text-[10px] font-mono uppercase tracking-widest mb-2">
                      <div className="p-2 border border-black bg-black text-white text-center">
                        <div className="text-base">{probeResult.items_read}</div>
                        <div>Lus</div>
                      </div>
                      <div className="p-2 border border-[var(--border-default)] text-center">
                        <div className="text-base text-[var(--text-muted)]">{probeResult.counts.stale}</div>
                        <div>Trop vieux</div>
                      </div>
                      <div className="p-2 border border-[var(--border-default)] text-center">
                        <div className="text-base text-[var(--text-muted)]">{probeResult.counts.no_match}</div>
                        <div>Sans match</div>
                      </div>
                      <div className={`p-2 border text-center ${probeResult.counts.would_match > 0 ? "border-emerald-600" : "border-amber-500"}`}>
                        <div className={`text-base font-semibold ${probeResult.counts.would_match > 0 ? "text-emerald-600" : "text-amber-600"}`}>
                          {probeResult.counts.would_match}
                        </div>
                        <div>Matcheraient</div>
                      </div>
                    </div>
                    <p className="text-xs text-[var(--text-muted)] leading-snug mb-1">
                      {probeResult.counts.would_match === 0
                        ? "Ce flux ne matche AUCUN mot-clé de la catégorie sur la fenêtre de fraîcheur. Envisage d'élargir les listes ou de renoncer à cette source."
                        : `${probeResult.counts.would_match} alerte(s) seraient créée(s) au prochain tick si tu ajoutes ce flux.`}
                    </p>
                    <ul className="max-h-56 overflow-y-auto divide-y divide-[var(--border-default)] border-t border-[var(--border-default)]">
                      {probeResult.items.filter((it) => it.reason === "would_match").slice(0, 5).map((it, i) => (
                        <li key={i} className="py-1.5 text-xs">
                          <div className="text-emerald-700 leading-snug">✓ {it.title}</div>
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {Object.values(it.matches || {}).flat().slice(0, 5).map((m, k) => (
                              <span key={k} className="text-[9px] font-mono px-1 py-0.5 bg-black text-white">{m}</span>
                            ))}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
          <ul className="divide-y divide-[var(--border-default)]">
            {feeds.map((f) => (
              <li key={f.id} className="py-3 flex items-start gap-2" data-testid="feed-row">
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${f.active ? "bg-[var(--status-success)]" : "bg-[var(--text-muted)]"}`} />
                    {f.active ? "actif" : "inactif"}
                  </div>
                  <div className="font-serif">{f.name}</div>
                  <div className="text-xs font-mono text-[var(--text-muted)] break-all">{f.url}</div>
                  {f.last_error && <div className="text-xs text-[var(--accent-strong)] mt-1">⚠ {f.last_error}</div>}
                </div>
                <button onClick={() => toggleFeed(f)}
                  className="min-h-[36px] px-2 border border-[var(--border-default)] hover:border-black"
                  data-testid="feed-toggle">
                  {f.active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => removeFeed(f)}
                  className="min-h-[36px] px-2 border border-[var(--border-default)] hover:bg-[var(--accent-strong)] hover:text-white hover:border-[var(--accent-strong)]"
                  data-testid="feed-delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* Keywords */}
        <section className="border border-[var(--border-default)] p-4 space-y-5 bg-white">
          <h3 className="font-serif text-lg">Mots-clés &amp; entités</h3>
          <ChipList label="Personnalités" testid="chips-personalities"
            items={cat.personalities} onChange={(v) => patch("personalities", v)} />
          <ChipList label="Organisations" testid="chips-parties"
            items={cat.parties} onChange={(v) => patch("parties", v)} />
          <ChipList label="Institutions" testid="chips-institutions"
            items={cat.institutions} onChange={(v) => patch("institutions", v)} />
          <ChipList label="Thèmes" testid="chips-themes"
            items={cat.themes} onChange={(v) => patch("themes", v)} />
          <ChipList label="Mots-clés URGENTS" testid="chips-urgent"
            items={cat.urgent_keywords} onChange={(v) => patch("urgent_keywords", v)} />
          <button onClick={() => save({
            personalities: cat.personalities, parties: cat.parties,
            institutions: cat.institutions, themes: cat.themes,
            urgent_keywords: cat.urgent_keywords,
          })} disabled={saving} data-testid="save-keywords"
            className="min-h-[44px] px-4 flex items-center gap-2 bg-black text-white text-[11px] uppercase tracking-widest font-mono hover:bg-[#222] disabled:opacity-60">
            <Save className="w-4 h-4" /> Enregistrer les mots-clés
          </button>
        </section>

        {/* Notifications */}
        <section className="border border-[var(--border-default)] p-4 space-y-3 bg-white">
          <h3 className="font-serif text-lg">Notifications</h3>
          <label className="flex items-center gap-3 min-h-[44px] text-sm">
            <input type="checkbox" checked={cat.push_enabled}
              onChange={(e) => patch("push_enabled", e.target.checked)}
              data-testid="cat-push-enabled" className="w-5 h-5 accent-black" />
            Envoyer des push web pour cette catégorie
          </label>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">
              Bot token {cat.telegram_bot_token_set && <span className="text-[var(--status-success)]">· ✓</span>}
            </div>
            <input type="password"
              placeholder={cat.telegram_bot_token_set ? "•••••• (laisser vide pour conserver)" : "123456:ABC-…"}
              value={tokenInput} onChange={(e) => setTokenInput(e.target.value)}
              data-testid="tg-token"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Chat ID standard</div>
            <input type="text" value={cat.telegram_chat_id || ""}
              onChange={(e) => patch("telegram_chat_id", e.target.value)}
              data-testid="tg-chat"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
          </div>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Chat ID urgent (optionnel)</div>
            <input type="text" value={cat.telegram_urgent_chat_id || ""}
              onChange={(e) => patch("telegram_urgent_chat_id", e.target.value)}
              data-testid="tg-urgent"
              className="w-full min-h-[44px] border border-[var(--border-default)] px-3 font-mono text-sm rounded-none focus:outline-none focus:border-black" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => {
              const body = {
                telegram_chat_id: cat.telegram_chat_id,
                telegram_urgent_chat_id: cat.telegram_urgent_chat_id,
                push_enabled: cat.push_enabled,
              };
              if (tokenInput.trim()) body.telegram_bot_token = tokenInput.trim();
              save(body).then(() => setTokenInput(""));
            }} disabled={saving} data-testid="save-notif"
              className="min-h-[44px] px-4 flex items-center gap-2 bg-black text-white text-[11px] uppercase tracking-widest font-mono hover:bg-[#222] disabled:opacity-60">
              <Save className="w-4 h-4" /> Enregistrer
            </button>
            <button onClick={testTg} data-testid="tg-test"
              className="min-h-[44px] px-4 flex items-center gap-2 border border-black bg-white hover:bg-black hover:text-white text-[11px] uppercase tracking-widest font-mono">
              <Send className="w-4 h-4" /> Test Telegram
            </button>
          </div>
        </section>

        <div>
          <button onClick={deleteCategory} data-testid="delete-cat"
            className="min-h-[44px] px-4 flex items-center gap-2 border border-[var(--accent-strong)] text-[var(--accent-strong)] hover:bg-[var(--accent-strong)] hover:text-white text-[11px] uppercase tracking-widest font-mono">
            <Trash2 className="w-4 h-4" /> Supprimer la catégorie
          </button>
        </div>
      </div>
    </div>
  );
}
